import type { Vehicle } from '../data/vehicles'
import { DOWN_PAYMENT_RATE, TENURE_YEARS } from './lease'

/* The two ways this showroom takes an order, and what each one asks for up
   front. A car is one price and one monthly figure; what differs between
   driving it away today and booking one is the first payment, and that is
   the only thing the picker on a card or a detail page switches.

   Which of the two a given trim-year is actually offered on is the showroom's
   to say: most can be had either way, while some are booking-only or on-spot.
   The console records that shared rule as `availability` on the variant-year
   quote, and every physical car linked to it inherits the same routes.

   Derived here rather than in either surface, so the card and the page it
   links to can never offer a visitor two different deals on the same car. */

export type PurchaseRoute = 'on-spot' | 'on-booking'

export type PurchaseOption = {
  route: PurchaseRoute
  /** 'On spot' — what the segment is labelled. */
  label: string
  /** The rupees asked up front: the showroom's own figure where it has set
      one, the house's standard share of the price otherwise. Null only on a
      car with no price to work either from — a zero would read as "nothing
      down", and that is the one case with nothing to quote. */
  downPayment: number | null
  /** True when the figure above is the house's standard terms rather than a
      figure the showroom typed for this car. */
  standard: boolean
  /** One line under the figure, explaining what choosing it means. */
  note: string
}

/** The routes this car is offered on, in the order they are offered.

    Two entries on a car that can be had either way, which is most of the
    floor — and the choice between them is the point of the picker. One entry
    on a car the showroom will only take the single way; that is not a car
    with a route missing, it is a car with one route, and the surfaces caption
    it as such rather than showing half a choice.

    A route the showroom has not typed a figure for is quoted on the house's
    standard terms — its share of this trim-year's price — rather than left blank.
    It is the same fallback the API already uses for the monthly instalment
    and the deposit that monthly assumes, so a car nobody has quoted by hand
    is still a car a visitor can read a first payment off; and because the
    quote belongs to the trim-year, every paint resolves the same amount.
    A figure the showroom has typed
    always wins: that is what a buyer was told at the desk. */
export function purchaseOptionsFor(vehicle: Vehicle): PurchaseOption[] {
  /* Nothing to work from on a car nobody has priced — the card says "on
     request" for the price there too. */
  const standard =
    Number.isFinite(vehicle.price) && vehicle.price > 0
      ? Math.round(vehicle.price * DOWN_PAYMENT_RATE)
      : null

  const stated = (value: number) =>
    Number.isFinite(value) && value > 0 ? value : null

  const onSpot: PurchaseOption = {
    route: 'on-spot',
    label: 'On spot',
    downPayment: stated(vehicle.onSpotDownPayment) ?? standard,
    standard: stated(vehicle.onSpotDownPayment) === null,
    note: 'Paid today — drive it off the floor.',
  }

  const onBooking: PurchaseOption = {
    route: 'on-booking',
    label: 'On booking',
    downPayment: stated(vehicle.bookingDownPayment) ?? standard,
    standard: stated(vehicle.bookingDownPayment) === null,
    note: 'Books this car and holds it for you.',
  }

  /* A car entered before the showroom started saying so has nothing recorded,
     and the way this floor has always worked is that both are on offer. */
  switch (vehicle.availability ?? 'both') {
    case 'on-spot':
      return [onSpot]
    case 'booking':
      return [onBooking]
    default:
      return [onSpot, onBooking]
  }
}

/** The line a car offered one way only is captioned with — "Available only on
    spot". Empty on a car offered both ways, which is captioned by the choice
    itself: two segments already say a visitor may take either. */
export function availabilityCaption(options: PurchaseOption[]) {
  if (options.length !== 1) return ''
  return `Available only ${options[0].label.toLowerCase()}`
}

/** The tenure the monthly figure runs for: the linked trim-year's where the
    showroom set one, the house's standard otherwise. */
export function tenureOf(vehicle: Vehicle) {
  return vehicle.instalmentYears > 0 ? vehicle.instalmentYears : TENURE_YEARS
}
